import Loading from '@/components/Loading';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { Dropdown, DropdownMenuDivider, DropdownMenuItem } from '@/components/UI/Dropdown';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar';
import { capitalise, errorToast, successToast } from '@/helpers';
import { _isNil } from '@/littledash';
import { DataTableTemplateApiId, DataTableTemplateInfoV1 } from '@/model/DataTable.model';
import InVivoError from '@/model/InVivoError.ts';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import { RouteService } from '@/support/RouteService';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { useModalAction } from '@/utils/modal';
import { FC, useMemo } from 'react';
import { RiMoreFill } from 'react-icons/ri';
import { useParams } from 'react-router-dom';

export const DataTableTemplatesList = () => {
  const { openModal, closeModal } = useModalAction();
  const { id: studyId } = useParams();

  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    query: { include: 'users' },
    options: { onError: { toast: true, slug: 'study-load' } },
  });

  const study = (studyResponse?.body as { data: Study })?.data;

  const isStudyWriteUser = Auth.isWriteUserForStudy(study as Study);
  const isTenantAdminUser = Auth.isCurrentTeamOwner();

  const { invoke: invokeDeleteDataTableTemplate } = useApiHook({
    endpoint: 'DELETE /api/v1/datatables/templates/{datatableTemplateApiId}',
    path: { datatableTemplateApiId: 'dtt_empty' },
    invokeOnInit: false,
  });
  const { invoke: invokePublishDataTableTemplate } = useApiHook({
    endpoint: 'POST /api/v1/datatables/templates/{datatableTemplateApiId}/publish',
    invokeOnInit: false,
    options: { onError: { toast: false, slug: 'data-table-template-publish', capture: false } },
  });
  const handleDataTableTemplatePublish = async (datatableTemplateApiId: DataTableTemplateApiId): Promise<boolean> =>
    new Promise((resolve) => {
      openModal('DATATABLE_TEMPLATE_PUBLISH', {
        closeModal: () => {
          closeModal();
          resolve(false);
        },
        publish: async ({ locked }: { locked: boolean }) => {
          try {
            await invokePublishDataTableTemplate({ path: { datatableTemplateApiId } });
            successToast('Template published');
            closeModal();
            resolve(true);
          } catch (err) {
            errorToast('Could not publish datatable template');
            const ivError = new InVivoError('Could not publish datatable template', {
              slug: 'publish-data-table-template',
              cause: err,
              context: { datatableTemplateApiId },
            });
            ExceptionHandler.captureException(ivError);
            resolve(false);
          }
        },
      });
    });
  const handleDataTableTemplateDelete = async (datatableTemplateApiId: DataTableTemplateApiId): Promise<boolean> =>
    new Promise((resolve) => {
      openModal('CONFIRM_DELETE_DATATABLE_TEMPLATE', {
        onClick: async () => {
          try {
            await invokeDeleteDataTableTemplate({ path: { datatableTemplateApiId } });
            successToast('Deleted template');
            resolve(true);
          } catch (err) {
            ExceptionHandler.captureException(
              new InVivoError('Could not delete data table template', {
                slug: 'delete-data-table-template',
                cause: err,
                context: { datatableTemplateApiId },
              })
            );
            errorToast('Could not delete data table template');
            resolve(false);
          }
        },
      });
    });

  const handleDataTableTemplateEdit = (
    dataTableTemplateName: string,
    dataTableTemplateApiId: DataTableTemplateApiId
  ): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      openModal('RENAME_DATA_TABLE_TEMPLATE', {
        dataTableTemplateName,
        dataTableTemplateApiId,
        closeModal: () => {
          closeModal();
          resolve(false);
        },
        onTemplateRename: () => resolve(true),
      });
    });

  const columns: Array<TanStackColumnDef<DataTableTemplateInfoV1>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        isVisible: true,
        enableSorting: true,
      },
      {
        id: 'created_at',
        header: 'Created on',
        accessorKey: 'created_at',
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: DataTableTemplateInfoV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original ? <DateTimeRenderer value={original.created_at} /> : null,
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        isVisible: true,
        enableSorting: false,
      },
      {
        id: 'created_by',
        header: 'Created by',
        isVisible: true,
        accessorKey: 'created_by',
        enableSorting: false,
        cell: ({ row: { original } }: { row: { original: DataTableTemplateInfoV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original && original.created_by ? (
            <UserAvatar user={original.created_by} className="ml1 dib" tooltip={original.created_by.name} />
          ) : null,
      },
      {
        id: 'updated_at',
        header: 'Updated on',
        accessorKey: 'updated_at',
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: DataTableTemplateInfoV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original ? <DateTimeRenderer value={original.updated_at} /> : null,
      },
      {
        id: 'visibility',
        header: 'Visibility',
        accessorKey: 'visibility',
        isVisible: true,
        enableSorting: false,
        cell: ({ row: { original } }: { row: { original: DataTableTemplateInfoV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original ? capitalise(original.visibility) : null,
      },
      {
        id: 'actions',
        isVisible: isStudyWriteUser,
        size: 35,
        enableSorting: false,
        cell: ({ table, row: { original } }: { table?: any; row: { original: DataTableTemplateInfoV1 } }) => (
          <TemplateListActions
            published={original.visibility === 'published'}
            handleDataTableTemplateEdit={() =>
              handleDataTableTemplateEdit(original.name, original.api_id).then((reload) =>
                reload ? table?.options?.meta?.apiTable?.fetchTableData() : Promise.resolve()
              )
            }
            handleDataTableTemplatePublish={() =>
              handleDataTableTemplatePublish(original.api_id).then((reload) =>
                reload ? table?.options?.meta?.apiTable?.fetchTableData() : Promise.resolve()
              )
            }
            handleDataTableTemplateDelete={() =>
              handleDataTableTemplateDelete(original.api_id).then((reload) =>
                reload ? table?.options?.meta?.apiTable?.fetchTableData() : Promise.resolve()
              )
            }
          />
        ),
      },
    ],
    [isTenantAdminUser, isStudyWriteUser, study]
  );

  if (studyLoading || _isNil(study)) {
    return (
      <div style={{ height: 600 }}>
        <Loading txt="Loading templates" />
      </div>
    );
  }

  return (
    <div className="h-100">
      <div className="">
        <APITable
          apiInfo={{
            type: 'internalApi',
            route: RouteService.api({
              endpoint: 'GET /api/v1/datatables/templates',
              path: undefined,
            }).url,
          }}
          columns={columns}
          defaultSortBy={{ id: 'created_at', desc: false }}
          searchPlaceholderText="Search"
          reduxTableName={'dataTableTemplates'}
          testIdPrefix={'dataTableTemplates'}
        />
      </div>
    </div>
  );
};

interface TemplateListActionProps {
  published: boolean;
  handleDataTableTemplateEdit: () => Promise<void>;
  handleDataTableTemplateDelete: () => Promise<void>;
  handleDataTableTemplatePublish: () => Promise<void>;
}

const TemplateListActions: FC<TemplateListActionProps> = ({
  published,
  handleDataTableTemplateEdit,
  handleDataTableTemplateDelete,
  handleDataTableTemplatePublish,
}) => {
  const isTenantAdminUser = Auth.isCurrentTeamOwner();
  if (!isTenantAdminUser && published) {
    return null;
  }

  return (
    <Dropdown
      renderMenu={() => (
        <>
          {!published ? <DropdownMenuItem onClick={handleDataTableTemplateEdit}>Edit</DropdownMenuItem> : null}
          {isTenantAdminUser && !published ? (
            <DropdownMenuItem onClick={handleDataTableTemplatePublish}>
              <span>Publish</span>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuDivider />
          <DropdownMenuItem onClick={handleDataTableTemplateDelete}>
            <span className="red">Delete</span>
          </DropdownMenuItem>
        </>
      )}
    >
      <RiMoreFill size={22} />
    </Dropdown>
  );
};
